import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { lineBreaksToNewLine, newlinesToLineBreak } from 'lib/helpers';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Emails,
  Form,
  FormGroup,
  FormSection,
  TextAreaWithEmoji,
  WorkloadSettings
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import { CalendarEventCertificates } from '.';

import type { CalendarEventExtended } from 'shared-types';

type FormState = Pick<
  CalendarEventExtended,
  | 'allowDeregistration'
  | 'CertificateTemplateId'
  | 'certificateAllowDownloading'
  | 'certificateCustomContent'
  | 'certificateLanguage'
  | 'certificateSendToOtherEmails'
  | 'certificateSendToOtherEmailsList'
  | 'certificateSendToUser'
  | 'markAutomaticParticipation'
  | 'message'
  | 'registrationEmailConfirmation'
  | 'registrationEmailForOthers'
  | 'registrationEmailForOthersEmails'
  | 'workloadIsEnabled'
  | 'workloadValue'
>;

const CalendarEventParticipantsSettings = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: {
      calendarEventCompletionAttributeConditions,
      calendarEventEnableCertificates
    }
  } = useReduxState();

  const [loading, setLoading] = useState(false);

  if (!calendarEvent) return null;

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoading(true);

      const data = { ...values };
      data.message = newlinesToLineBreak(data.message);

      await api.put(`/admin/calendar-events/${calendarEvent.id}`, {
        ...data,
        CertificateTemplateId: data.CertificateTemplateId || null
      });
      dispatch.updatePageState({
        calendarEvent: { ...calendarEvent, ...values },
        hasUnsavedChanges: null
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const requireWorkload = calendarEventCompletionAttributeConditions.some(
    (condition) => {
      return (
        condition.requireWorkload &&
        calendarEvent.customAttributes?.[condition.key] === condition.value
      );
    }
  );

  const initialValues: FormState = {
    allowDeregistration: calendarEvent.allowDeregistration,
    CertificateTemplateId: calendarEvent.CertificateTemplateId || 0,
    certificateAllowDownloading: calendarEvent.certificateAllowDownloading,
    certificateCustomContent: calendarEvent.certificateCustomContent,
    certificateLanguage: calendarEvent.certificateLanguage,
    certificateSendToOtherEmails: calendarEvent.certificateSendToOtherEmails,
    certificateSendToOtherEmailsList:
      calendarEvent.certificateSendToOtherEmailsList,
    certificateSendToUser: calendarEvent.certificateSendToUser,
    markAutomaticParticipation: calendarEvent.markAutomaticParticipation,
    message: lineBreaksToNewLine(calendarEvent.message || ''),
    registrationEmailConfirmation: calendarEvent.registrationEmailConfirmation,
    registrationEmailForOthers: calendarEvent.registrationEmailForOthers,
    registrationEmailForOthersEmails:
      calendarEvent.registrationEmailForOthersEmails,
    workloadIsEnabled: requireWorkload ? true : calendarEvent.workloadIsEnabled,
    workloadValue: calendarEvent.workloadValue
  };

  const participantsInfoMessageHelpText =
    calendarEvent.visibility === 'public'
      ? messages.eventCalendarParticipantInfoDescriptionPublic
      : messages.eventCalendarParticipantInfoDescription;

  return (
    <Box padding={0} title={messages.eventCalendarParticipantsSettings}>
      <Form
        allowEmptyStrings={true}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ formState }) => (
          <>
            <FormSection maxWidth="100%">
              <UnsavedFormChanges initialValues={initialValues} />

              <FormGroup label={messages.emailConfirmation}>
                <Checkbox
                  id="registration-email-confirmation"
                  field="registrationEmailConfirmation"
                  label={messages.calendarEventSendEmailConfirmation}
                />
                <Checkbox
                  id="registration-email-for-others"
                  field="registrationEmailForOthers"
                  label={messages.calendarEventSendEmailForOthers}
                  style={{ marginTop: 10 }}
                />
                <Emails
                  disabled={!formState.values.registrationEmailForOthers}
                  field="registrationEmailForOthersEmails"
                  id="registration-email-for-others-emails"
                  style={{ marginLeft: 20, marginTop: 8 }}
                />
              </FormGroup>

              <FormGroup
                label={messages.eventCalendarParticipantInfo}
                labelHelpText={participantsInfoMessageHelpText}
              >
                <div>
                  <TextAreaWithEmoji
                    textAreaProps={{
                      autoGrow: true,
                      id: 'message',
                      field: 'message',
                      maxLength: 3000
                    }}
                  />
                </div>
              </FormGroup>

              <FormGroup label={messages.commonCancellation}>
                <Checkbox
                  id="allowDeregistration"
                  field="allowDeregistration"
                  label={messages.eventCalendarEventParticipationDeregister}
                  style={{ marginBottom: 5 }}
                />
              </FormGroup>

              <FormGroup
                label={messages.eventCalendarEventParticipationAutomatic}
              >
                <Checkbox
                  id="markAutomaticParticipation"
                  field="markAutomaticParticipation"
                  label={messages.eventCalendarEventParticipationMark}
                />
              </FormGroup>

              <WorkloadSettings
                disabled={requireWorkload}
                fieldWorkloadEnabled="workloadIsEnabled"
                fieldWorkloadValue="workloadValue"
                workloadEnabledProps={{ label: messages.calendarEventWorkload }}
                workloadValueProps={{
                  label: messages.calendarEventWorkloadTime
                }}
              />
            </FormSection>

            {calendarEventEnableCertificates && <CalendarEventCertificates />}
            <FormSection>
              <FormGroup>
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={loading}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              </FormGroup>
            </FormSection>
          </>
        )}
      </Form>
    </Box>
  );
};

export default CalendarEventParticipantsSettings;
